import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Meter track — the full-width background rail. Size controls height only;
 * color is always neutral (bg-surface-2 / border-line), matching Stat's card
 * treatment so the fill color is the only semantic signal.
 */
export const meterTrackVariants = cva(
  'relative w-full overflow-hidden rounded-full bg-surface-2 border border-line',
  {
    variants: {
      size: {
        sm: 'h-1.5',
        md: 'h-2.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

/**
 * Meter fill — width is set inline by the component (value is continuous,
 * not a fixed variant). Color maps 1:1 to MasteryState via the same semantic
 * tokens used elsewhere in the DS (green/gold/red, Stat/Badge convention):
 *   strong -> green ("Solido")
 *   consolidate -> gold ("Da consolidare")
 *   weak -> red ("Fragile")
 *   insufficient -> ink-3 neutral ("Ancora pochi dati" — data EXISTS but is
 *     too scarce to trust)
 *   neutral -> ink-3 neutral, same fill as `insufficient` but a different
 *     semantic: plain progress that was never meant to carry a quality
 *     verdict at all (e.g. Panoramica's "polso" ratios — sections read,
 *     last quiz score). Never use `insufficient` for this case: it reads as
 *     "not enough data" on data that is perfectly complete.
 *
 * `provisional` (answered < reliability cutoff) dims the fill; the
 * diagonal hatch overlay itself is applied by the platform component (inline
 * style on web, drawn Views on native) since CSS background-image gradients
 * have no RN equivalent without an external dependency.
 */
export const meterFillVariants = cva(
  'h-full rounded-full transition-[width] duration-300 ease-out motion-reduce:transition-none',
  {
    variants: {
      state: {
        strong: 'bg-green',
        consolidate: 'bg-gold',
        weak: 'bg-red',
        insufficient: 'bg-ink-3',
        neutral: 'bg-ink-3',
      },
      provisional: {
        true: 'opacity-[0.55]',
        false: '',
      },
    },
    defaultVariants: {
      state: 'insufficient',
      provisional: false,
    },
  },
)

export type MeterTrackVariantProps = VariantProps<typeof meterTrackVariants>
export type MeterFillVariantProps = VariantProps<typeof meterFillVariants>
export type MeterState = NonNullable<MeterFillVariantProps['state']>
export type MeterSize = NonNullable<MeterTrackVariantProps['size']>
